import RestService from "../../../core/services/rest.service";
import type { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { CSR } from "~/modules/certificate/models/CSR.interface";
import type { KeystoreCSRRequest } from "~/modules/certificate/models/KeystoreCSRRequest.interface";
import type { KeystoreCertificate } from "~/modules/certificate/models/KeystoreCertificate.interface";
import type { KeystoreCertificateDetail } from "~/modules/certificate/models/KeystoreCertificateDetail.interface";
import type { KeystoreCertificateSubject } from "~/modules/certificate/models/KeystoreCertificateSubject.interface";
import type { KeystoreSelfSignedCertificateRequest } from "~/modules/certificate/models/KeystoreSelfSignedCertificateRequest.interface";

export class KeystoreService extends RestService {
  protected BASE_URL = "/keystore";
  protected CSR_PATH = "/csr";
  protected CERTIFICATE_PATH = "/certificate";
  protected PARSE_CERTIFICATE_PATH = "/parse-certificate";
  /**
   * Get a list of certificates on Keystore
   * @param page
   * @param pageSize
   * @param search
   * @param sort
   * @return List of keystore certificates with context
   */
  getListOfCertificates({
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<KeystoreCertificate>> {
    const params = { page, pageSize, filter: searchParams, sort };
    return this.$api.get(this.BASE_URL, { params });
  }

  /**
   * Get a certificate by its alias
   * @param alias Identifier of the certificate
   * @returns The certificate detail for the alias
   */
  getCertificate(alias: string): Promise<KeystoreCertificateDetail> {
    return this.$api.get(`${this.BASE_URL}/${alias}`);
  }

  getCsr(alias: string): Promise<CSR> {
    return this.$api.get(`${this.BASE_URL}/${alias}/csr`);
  }

  /**
   * Parse a certificate on backend
   * Do not manage progress
   * @param certificate
   */
  parseCertificate(certificate: File): Promise<KeystoreCertificateSubject> {
    const formData = new FormData();
    formData.append("certificate", certificate, certificate.name);

    const config: AxiosRequestConfig<FormData> = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    return this.$api.post(
      `${this.BASE_URL}${this.PARSE_CERTIFICATE_PATH}`,
      formData,
      config,
    );
  }

  /**
   * Request a self-signed certificate
   * @param certificateRequest self-signed certificate request data
   */
  generateSelfSignedCertificate(
    certificateRequest: KeystoreSelfSignedCertificateRequest,
  ): Promise<void> {
    const config = {
      headers: {
        "Content-type": "application/json",
        Accept: "application/json",
      },
    };

    return this.$api.post(
      `${this.BASE_URL}${this.CERTIFICATE_PATH}`,
      { certificateRequest },
      config,
    );
  }

  /**
   * Request a CSR
   * @param csrRequest the CSR request data
   * @returns the CSR for the alias
   */
  generateCertificateSigningRequest(
    csrRequest: KeystoreCSRRequest,
  ): Promise<CSR> {
    const config = {
      headers: {
        "Content-type": "application/json",
        Accept: "application/json",
      },
    };

    return this.$api.post(
      `${this.BASE_URL}${this.CSR_PATH}`,
      csrRequest,
      config,
    );
  }

  /**
   * Post a certificate update into keystore for an alias
   * @param alias Identifier of the certificate
   * @param progress
   * @param certificate certificate data
   * @param tags Array of objects (Root Type for Tags)
   * @param automaticallyRenewed
   */
  updateCertificate(
    alias: string,
    progress: Ref<number | undefined | null>,
    tags: string[],
    certificate?: File,
    automaticallyRenewed?: boolean,
  ): Promise<void> {
    const formData = new FormData();
    const isUploading = certificate != null;
    if (isUploading) {
      formData.append("certificate", certificate, certificate.name);
    }

    if (tags.length) {
      tags.forEach((tag) => formData.append("tags[]", tag));
    } else {
      formData.append("tags[]", "");
    }

    if (automaticallyRenewed !== undefined) {
      formData.append("automaticallyRenewed", automaticallyRenewed.toString());
    }

    const config: AxiosRequestConfig<FormData> = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (isUploading) {
          progress.value = progressEvent.total
            ? (progressEvent.loaded / progressEvent.total) * 100
            : 0;
        }
      },
    };

    return this.$api.put(
      `${this.BASE_URL}${this.CERTIFICATE_PATH}/${alias}`,
      formData,
      config,
    );
  }

  /**
   * Delete a key entry by alias
   * @param alias Identifier of the certificate
   */
  deleteCertificate(alias: string): Promise<void> {
    return this.$api.delete(`${this.BASE_URL}/${alias}`);
  }

  instantCertificateRenewCsr(
    alias: string,
  ): Promise<KeystoreCertificateDetail> {
    return this.$api.post(`${this.BASE_URL}/${alias}/renew`);
  }
}

export default new KeystoreService();
